import type { SchedulingJobType } from '../SchedulingTypes';
import { truncate } from 'services/utils/truncate';

/*
 * appointmentText.ts
 * Builds the text shown in a scheduled appointment block of the
 * employee list, the employee timeline and the personal calendar
 * from the appointment text setting of the division. The setting
 * holds the chosen options in their display order.
 */

// The option keys of the appointment text setting, matching the backend
export type AppointmentTextOption =
    | 'client'
    | 'work_location_address'
    | 'work_location_city'
    | 'job_number'
    | 'description'
    | 'project_number'
    | 'project_name';

// The options a user can pick, with their label and example translation keys
export const APPOINTMENT_TEXT_OPTIONS: { value: AppointmentTextOption; label: string; example: string }[] = [
    { value: 'client', label: 'scheduling.settings.appointment_text_options.client', example: 'scheduling.settings.appointment_text_examples.client' },
    { value: 'work_location_address', label: 'scheduling.settings.appointment_text_options.work_location_address', example: 'scheduling.settings.appointment_text_examples.work_location_address' },
    { value: 'work_location_city', label: 'scheduling.settings.appointment_text_options.work_location_city', example: 'scheduling.settings.appointment_text_examples.work_location_city' },
    { value: 'job_number', label: 'scheduling.settings.appointment_text_options.job_number', example: 'scheduling.settings.appointment_text_examples.job_number' },
    { value: 'description', label: 'scheduling.settings.appointment_text_options.description', example: 'scheduling.settings.appointment_text_examples.description' },
    { value: 'project_number', label: 'scheduling.settings.appointment_text_options.project_number', example: 'scheduling.settings.appointment_text_examples.project_number' },
    { value: 'project_name', label: 'scheduling.settings.appointment_text_options.project_name', example: 'scheduling.settings.appointment_text_examples.project_name' },
];

// The default text when nothing valid is stored, matching the backend default
export const DEFAULT_APPOINTMENT_TEXT: AppointmentTextOption[] = ['work_location_address'];

// The maximum length of the short description in the block
export const APPOINTMENT_DESCRIPTION_LENGTH = 35;

// The separator between the text parts of a block
export const APPOINTMENT_TEXT_SEPARATOR = ' • ';

// Resolves a stored setting value to a clean list of known options
export const resolveAppointmentTextOptions = (value: unknown): AppointmentTextOption[] => {
    // Fall back to the default for values which are not a list
    if (!Array.isArray(value)) return DEFAULT_APPOINTMENT_TEXT;

    // Keep the known options in their stored order without duplicates
    const knownOptions = APPOINTMENT_TEXT_OPTIONS.map(option => option.value);
    const options = value.filter(
        (option, index) => knownOptions.includes(option) && value.indexOf(option) === index
    ) as AppointmentTextOption[];

    // Fall back to the default when nothing valid is stored
    return options.length > 0 ? options : DEFAULT_APPOINTMENT_TEXT;
};

// Builds the work location address as street, house number and city
const buildWorkLocationAddress = (job: SchedulingJobType): string => {
    const streetLine = [job.work_location_street, job.work_location_house_number].filter(Boolean).join(' ');
    return [streetLine, job.work_location_city].filter(Boolean).join(', ');
};

// Resolves the value of one option for the given job
const resolveOptionValue = (job: SchedulingJobType, option: AppointmentTextOption): string => {
    switch (option) {
        case 'client':
            return job.client_name ?? '';
        case 'work_location_address':
            return buildWorkLocationAddress(job);
        case 'work_location_city':
            return job.work_location_city ?? '';
        case 'job_number':
            return job.number ?? '';
        case 'description':
            return truncate(job.main_description, APPOINTMENT_DESCRIPTION_LENGTH);
        case 'project_number':
            return job.project_number ?? '';
        case 'project_name':
            return job.project_name ?? '';
        default:
            return '';
    }
};

// Builds the text parts of an appointment block in the chosen order
export const getAppointmentTextParts = (job: SchedulingJobType | undefined, options: AppointmentTextOption[]): string[] => {
    if (!job) return [];

    // Resolve every chosen option and drop the empty ones
    const parts = options.map(option => resolveOptionValue(job, option)).filter(part => part !== '');
    if (parts.length > 0) return parts;

    // Fall back to the client name or the job number so a block never stays blank
    const fallback = job.client_name || job.number;
    return fallback ? [fallback] : [];
};

// Builds the full text of an appointment block
export const formatAppointmentText = (job: SchedulingJobType | undefined, options: AppointmentTextOption[]): string => {
    return getAppointmentTextParts(job, options).join(APPOINTMENT_TEXT_SEPARATOR);
};
